import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, KeyRound, MailCheck, Copy } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ColorPicker } from "@/pages/ColorListPage";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";

export default function MembersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", role: "standard", color: "#145c8c",
  });
  const [inviteResult, setInviteResult] = useState(null); // { link, delivered, email }

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== "admin") return <Navigate to="/timer" replace />;

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", email: "", role: "standard", color: "#145c8c" });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ name: it.name, email: it.email, role: it.role, color: it.color || "#145c8c" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, color: form.color };
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Member updated");
        setOpen(false);
        load();
      } else {
        // Invite flow — no password sent; backend emails the invite
        const { data } = await api.post("/users", {
          name: form.name,
          email: form.email,
          role: form.role,
          color: form.color,
        });
        setOpen(false);
        load();
        if (data.linked_existing) {
          toast.success(`${data.name || data.email} was already a Dreamtime user — added to this organisation.`);
        } else if (data.email_delivered) {
          toast.success(`Invitation sent to ${form.email}`);
        } else {
          // Show a copyable link if email isn't configured / failed
          toast.error(`Email delivery to ${form.email} failed — copy the link to share manually.`);
          setInviteResult({ link: data.invite_link, delivered: false, email: form.email, purpose: "invite" });
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (it) => {
    const isMulti = Array.isArray(it.organisation_ids) && it.organisation_ids.length > 1;
    const msg = isMulti
      ? `Remove ${it.name || it.email} from this organisation? Their account and entries in other organisations are kept.`
      : `Delete ${it.name || it.email}? Their time entries in every organisation will also be removed.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/users/${it.id}`);
      load();
      toast.success(data?.unlinked ? "Removed from this organisation" : "Member deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const sendReset = async (it) => {
    const isInvite = !!it.pending_password;
    const verb = isInvite ? "Resend the invitation email" : "Send a password reset email";
    if (!window.confirm(`${verb} to ${it.email}?`)) return;
    try {
      const { data } = await api.post(`/users/${it.id}/send-reset`);
      if (data.email_delivered) {
        toast.success(isInvite
          ? `Invitation re-sent to ${it.email}`
          : `Reset email sent to ${it.email}`);
      } else {
        setInviteResult({ link: data.reset_link, delivered: false, email: it.email, purpose: data.purpose || "reset" });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-8">
      <div className="max-w-5xl mx-auto w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>Members</h1>
          <p className="text-sm text-slate-500 mt-1">Add team members by email — they&apos;ll get a link to set their own password.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="text-white" style={{ background: "#145c8c" }}
                    data-testid="member-add-button">
              <Plus className="h-4 w-4 mr-1" /> Invite Member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Member" : "Invite Member"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       className="mt-1" data-testid="member-name-input" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} disabled={!!editing}
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       className="mt-1" data-testid="member-email-input" />
                {!editing && (
                  <p className="text-xs text-slate-400 mt-1">
                    We&apos;ll email them a secure link to set their password (valid 24 hours).
                  </p>
                )}
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1" data-testid="member-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Colour</Label>
                <div className="mt-2">
                  <ColorPicker value={form.color} onChange={(c) => setForm({ ...form, color: c })}
                               testId="member-color-picker" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="text-white" style={{ background: "#145c8c" }}
                      data-testid="member-save-button">
                {editing ? "Save" : "Send invitation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Member</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`member-row-${it.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                         style={{ background: it.color || "#145c8c" }}>
                      {(it.name || it.email).slice(0, 2).toUpperCase()}
                    </div>
                    <span className="font-medium">{it.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{it.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    it.role === "admin"
                      ? "bg-[#e8f1f8] text-[#0b2c47]"
                      : "bg-slate-100 text-slate-600"
                  }`}>{it.role}</span>
                </td>
                <td className="px-4 py-3">
                  {it.pending_password ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                      <MailCheck className="h-3 w-3" /> Invite pending
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">Active</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => sendReset(it)}
                          title={it.pending_password ? "Resend invite email" : "Send password reset email"}
                          className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500 hover:text-[#145c8c]"
                          data-testid={`member-reset-${it.id}`}>
                    {it.pending_password ? <MailCheck className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
                  </button>
                  <button onClick={() => openEdit(it)} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500"
                          data-testid={`member-edit-${it.id}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(it)} className="p-1.5 hover:bg-red-50 rounded-md text-red-500"
                          data-testid={`member-delete-${it.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Fallback dialog shown when email wasn't delivered (no Resend key configured) */}
      <Dialog open={!!inviteResult} onOpenChange={(v) => !v && setInviteResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {inviteResult?.purpose === "reset" ? "Password reset link" : "Invitation link"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Email delivery is not configured. Copy this link and send it to <span className="font-medium">{inviteResult?.email}</span>. It expires in 24 hours.
          </p>
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md p-2 mt-2">
            <input
              readOnly
              value={inviteResult?.link || ""}
              className="flex-1 bg-transparent text-xs font-mono text-slate-700 outline-none"
              data-testid="invite-link-display"
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(inviteResult?.link || "");
                toast.success("Link copied");
              }}
              className="p-1.5 hover:bg-slate-200 rounded-md text-slate-500"
              data-testid="invite-link-copy"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <DialogFooter>
            <Button onClick={() => setInviteResult(null)} className="text-white" style={{ background: "#145c8c" }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
